import React, { useCallback, useEffect, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { MagnifyingGlass, MapPin, Crosshair } from "@phosphor-icons/react";
import ReactionChainsStrip from "../components/ReactionChainsStrip";
import CommunitiesStrip from "../components/CommunitiesStrip";

const FALLBACKS = [
  "https://images.unsplash.com/photo-1767801829041-f5243a34525d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1621826269707-5b27afe47c79?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1734448824631-458e1b9a8b5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
];

export default function Explore() {
  const [posts, setPosts] = useState([]);
  const [res, setRes] = useState({ creators: [], sounds: [], videos: [], hashtags: [], communities: [] });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [mode, setMode] = useState("trending"); // "trending" | "nearby"
  const [nearby, setNearby] = useState([]);
  const [nearLabel, setNearLabel] = useState("");
  const [nearState, setNearState] = useState("idle"); // idle | locating | loading | ready | denied | error
  const [nearLoaded, setNearLoaded] = useState(false);

  const loadNearby = useCallback(() => {
    if (!navigator.geolocation) { setNearState("error"); return; }
    setNearState("locating");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setNearState("loading");
        try {
          const [posRes, geoRes] = await Promise.all([
            api.get("/posts/nearby", { params: { lat: latitude, lng: longitude, radius_km: 50 } }),
            api.get("/geo/reverse", { params: { lat: latitude, lng: longitude } }),
          ]);
          setNearby(posRes.data || []);
          setNearLabel(geoRes.data?.label || "");
          setNearState("ready");
          setNearLoaded(true);
        } catch {
          setNearState("error");
        }
      },
      () => setNearState("denied"),
      { enableHighAccuracy: false, timeout: 8000 },
    );
  }, []);

  const openNearby = () => {
    setMode("nearby");
    if (!nearLoaded && nearState === "idle") loadNearby();
  };

  useEffect(() => {
    api.get("/search/suggestions", { params: { limit: 8 } })
      .then(({ data }) => setSuggestions(data.enabled ? (data.hashtags || []) : []))
      .catch(() => {});
  }, []);

  const loadExplore = useCallback(async () => {
    try {
      const { data } = await api.get("/posts/explore");
      setPosts(data);
    } catch (err) {
      console.error("explore load failed", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadExplore(); }, [loadExplore]);

  useEffect(() => {
    if (!q.trim()) { setRes({ creators: [], sounds: [], videos: [], hashtags: [], communities: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q, type: "all" } });
        setRes({ creators: data.creators || [], sounds: data.sounds || [], videos: data.videos || [], hashtags: data.hashtags || [], communities: data.communities || [] });
      } catch (err) {
        console.error("search failed", err);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const searching = q.trim().length > 0;

  return (
    <div className="min-h-[100dvh] bg-ink pb-32">
      <header className="sticky top-0 z-30 crystal-glass px-5 pt-6 pb-4">
        <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Discover</p>
        <div className="flex items-center justify-between">
          <h1 className="text-cream text-3xl font-black tracking-tighter">Explore the wave</h1>
          <Link to="/trending" data-testid="explore-to-trending" className="px-3 py-1.5 rounded-full bg-papaya/20 border border-papaya/40 text-papaya text-[10px] font-black tracking-[0.2em] uppercase hover:bg-papaya/30 transition-colors">
            # Trending
          </Link>
        </div>
        <div className="mt-4 flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5">
          <MagnifyingGlass size={18} className="text-cream/50" />
          <input
            data-testid="explore-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Creators, sounds, videos, #tags…"
            className="flex-1 bg-transparent text-cream placeholder:text-cream/40 focus:outline-none text-sm"
          />
        </div>
      </header>

      {searching ? (
        <div className="px-5 mt-4 space-y-6" data-testid="search-results">
          {res.communities.length > 0 && (
            <section>
              <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">Communities</p>
              <div className="space-y-2">
                {res.communities.map((c) => (
                  <Link key={c.slug} to={`/c/${c.slug}`} data-testid={`search-community-${c.slug}`} className="flex items-center gap-3 p-2.5 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/50 transition-colors">
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/10 flex-shrink-0">
                      <img src={c.cover} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-cream font-bold">{c.emoji} {c.name}</p>
                      <p className="text-cream/50 text-xs truncate">{c.tagline} · {(c.post_count || 0).toLocaleString()} posts</p>
                    </div>
                    <span className="text-papaya text-[10px] font-black tracking-[0.2em] uppercase">Hub</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
          {res.creators.length > 0 && (
            <section>
              <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">Creators</p>
              <div className="space-y-2">
                {res.creators.map((u) => (
                  <Link key={u.id} to={`/u/${u.username}`} data-testid={`search-creator-${u.username}`} className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/50 transition-colors">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-papaya to-sea flex items-center justify-center text-ink font-black flex-shrink-0">
                      {u.avatar_path ? <img src={fileUrl(u.avatar_path)} alt="" className="w-full h-full object-cover" /> : (u.display_name || u.username)[0].toUpperCase()}
                    </div>
                    <div className="flex-1"><p className="text-cream font-bold">{u.display_name}</p><p className="text-cream/50 text-xs">@{u.username}</p></div>
                  </Link>
                ))}
              </div>
            </section>
          )}
          {res.hashtags.length > 0 && (
            <section>
              <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">Hashtags</p>
              <div className="flex flex-wrap gap-2">
                {res.hashtags.map((h) => (
                  <Link key={h.tag} to={`/tag/${h.tag}`} data-testid={`search-hashtag-${h.tag}`} className="px-3 py-2 rounded-full bg-white/5 border border-white/10 text-cream text-sm font-bold hover:border-papaya/50">#{h.tag} <span className="text-cream/40 text-xs">· {h.count}</span></Link>
                ))}
              </div>
            </section>
          )}
          {res.sounds.length > 0 && (
            <section>
              <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">Sounds</p>
              <div className="space-y-2">
                {res.sounds.map((snd) => (
                  <Link key={snd.id} to={`/create?soundId=${snd.id}`} data-testid={`search-sound-${snd.id}`} className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/5 hover:border-papaya/50 transition-colors">
                    <div className="w-11 h-11 rounded-xl bg-papaya/20 flex items-center justify-center text-lg">🎵</div>
                    <div className="flex-1 min-w-0"><p className="text-cream font-bold truncate">{snd.title}</p><p className="text-cream/50 text-xs truncate">{snd.artist || "Sound"}{snd.mood ? ` · ${snd.mood}` : ""}</p></div>
                    <span className="text-papaya text-[10px] font-black tracking-[0.2em] uppercase">Use</span>
                  </Link>
                ))}
              </div>
            </section>
          )}
          {res.videos.length > 0 && (
            <section>
              <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3">Videos</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {res.videos.map((p) => (
                  <Link key={p.id} to={`/p/${p.id}`} data-testid={`search-video-${p.id}`} className="group relative aspect-[9/14] rounded-2xl overflow-hidden bg-white/5">
                    {p.media_type === "video" ? <video src={fileUrl(p.media_path)} className="w-full h-full object-cover" muted /> : <img src={p.media_path ? fileUrl(p.media_path) : FALLBACKS[0]} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-ink to-transparent"><p className="text-cream text-[11px] font-bold line-clamp-1">@{p.author?.username}</p></div>
                  </Link>
                ))}
              </div>
            </section>
          )}
          {!res.creators.length && !res.sounds.length && !res.videos.length && !res.hashtags.length && !res.communities.length && (
            <p className="text-cream/40 text-sm text-center py-10">No results for “{q}”.</p>
          )}
        </div>
      ) : (
        <>
          <div className="px-5 mt-4 flex gap-2" data-testid="explore-mode-toggle">
            <button
              data-testid="explore-mode-trending"
              onClick={() => setMode("trending")}
              className={`flex-1 py-2.5 rounded-full text-xs font-black tracking-[0.2em] uppercase transition-colors ${mode === "trending" ? "bg-papaya text-ink" : "bg-white/5 border border-white/10 text-cream/60 hover:text-cream"}`}
            >
              Trending
            </button>
            <button
              data-testid="explore-mode-nearby"
              onClick={openNearby}
              className={`flex-1 py-2.5 rounded-full text-xs font-black tracking-[0.2em] uppercase transition-colors flex items-center justify-center gap-1.5 ${mode === "nearby" ? "bg-papaya text-ink" : "bg-white/5 border border-white/10 text-cream/60 hover:text-cream"}`}
            >
              <MapPin size={13} weight="fill" /> Near me
            </button>
          </div>

          {mode === "nearby" ? (
            <section className="px-3 mt-5" data-testid="explore-nearby">
              {nearState === "locating" && <p className="text-cream/50 px-2 text-sm">Finding your location…</p>}
              {nearState === "loading" && <p className="text-cream/50 px-2 text-sm">Loading nearby posts…</p>}
              {nearState === "denied" && (
                <div className="px-2 py-8 text-center">
                  <MapPin size={28} className="text-cream/30 mx-auto" />
                  <p className="text-cream/60 text-sm mt-3">Location permission is off.</p>
                  <p className="text-cream/40 text-xs mt-1">Allow location in your browser to discover nearby content.</p>
                  <button data-testid="explore-nearby-retry" onClick={loadNearby} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-papaya/20 border border-papaya/40 text-papaya text-[11px] font-black tracking-[0.15em] uppercase">
                    <Crosshair size={13} weight="fill" /> Try again
                  </button>
                </div>
              )}
              {nearState === "error" && (
                <div className="px-2 py-8 text-center">
                  <p className="text-cream/60 text-sm">Couldn't load nearby posts.</p>
                  <button data-testid="explore-nearby-retry" onClick={loadNearby} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-papaya/20 border border-papaya/40 text-papaya text-[11px] font-black tracking-[0.15em] uppercase">
                    <Crosshair size={13} weight="fill" /> Retry
                  </button>
                </div>
              )}
              {nearState === "ready" && (
                <>
                  {nearLabel && (
                    <p className="text-cream/60 text-xs px-2 mb-3 flex items-center gap-1.5"><MapPin size={13} weight="fill" className="text-papaya" /> Near {nearLabel}</p>
                  )}
                  {nearby.length === 0 ? (
                    <p className="text-cream/40 px-2 text-sm">No public posts near you yet — be the first to tag your spot.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="explore-nearby-grid">
                      {nearby.map((p, i) => (
                        <Link key={p.id} to={`/p/${p.id}`} data-testid={`nearby-post-${p.id}`} className="group relative aspect-[9/14] rounded-2xl overflow-hidden bg-white/5">
                          {p.media_type === "video" ? (
                            <video src={fileUrl(p.media_path)} className="w-full h-full object-cover" muted />
                          ) : (
                            <img src={p.media_path ? fileUrl(p.media_path) : FALLBACKS[i % FALLBACKS.length]} alt="" className="w-full h-full object-cover" />
                          )}
                          <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-ink to-transparent">
                            {p.location && <p className="text-papaya text-[10px] font-bold flex items-center gap-1"><MapPin size={10} weight="fill" />{p.location}</p>}
                            <p className="text-cream text-xs font-bold line-clamp-1">@{p.author?.username}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </section>
          ) : (
          <>
          {suggestions.length > 0 && (
            <section className="px-5 mt-4" data-testid="explore-suggestions">
              <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-2">Suggested</p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {suggestions.map((h) => (
                  <button
                    key={h.tag}
                    onClick={() => setQ(h.tag)}
                    data-testid={`suggested-tag-${h.tag}`}
                    className="flex-shrink-0 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-cream text-sm font-bold hover:border-papaya/50 transition-colors"
                  >
                    #{h.tag}
                  </button>
                ))}
              </div>
            </section>
          )}
          <ReactionChainsStrip />
          <CommunitiesStrip />
          <section className="px-4 mt-5">
            <Link to="/dance" data-testid="explore-dance-banner" className="relative flex items-center gap-3 rounded-3xl overflow-hidden border border-jam-gold/40 p-4 hover:border-jam-gold/70 transition-colors">
              <div className="absolute inset-0 bg-gradient-to-r from-papaya/25 via-ink to-jam-gold/15" />
              <div className="relative w-12 h-12 rounded-2xl bg-jam-gold/20 flex items-center justify-center text-2xl">🏆</div>
              <div className="relative flex-1 min-w-0">
                <p className="text-cream font-black tracking-tight">Dance Hub</p>
                <p className="text-cream/70 text-xs">Weekly competitions &amp; challenges — enter &amp; win the crown</p>
              </div>
              <span className="relative text-jam-gold text-[11px] font-black tracking-[0.15em] uppercase">Open →</span>
            </Link>
          </section>
          <section className="px-3 mt-5">
            <p className="text-cream/60 text-xs tracking-[0.2em] uppercase font-bold mb-3 px-2">Trending</p>
            {loading ? (
              <p className="text-cream/40 px-2">Loading…</p>
            ) : posts.length === 0 ? (
              <p className="text-cream/40 px-2 text-sm">No posts yet — be the first.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="explore-grid">
                {posts.map((p, i) => (
                  <Link key={p.id} to={`/p/${p.id}`} data-testid={`explore-post-${p.id}`} className="group relative aspect-[9/14] rounded-2xl overflow-hidden bg-white/5">
                    {p.media_type === "video" ? (
                      <video src={fileUrl(p.media_path)} className="w-full h-full object-cover" muted />
                    ) : (
                      <img src={p.media_path ? fileUrl(p.media_path) : FALLBACKS[i % FALLBACKS.length]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-ink to-transparent">
                      <p className="text-cream text-xs font-bold line-clamp-2">{p.caption}</p>
                      <p className="text-cream/60 text-[10px] mt-1">@{p.author?.username} · ❤︎ {p.likes_count}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
          </>
          )}
        </>
      )}
    </div>
  );
}
